
import { Head, router } from '@inertiajs/react';
import MainLayout from '../../components/MainLayout';
import Container from '../../components/Container';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2 } from 'lucide-react';
import { ExclusiveProduct, StoreSettings } from '../../types';

interface CartItem {
  id: number;
  quantity: number;
  product: {
    id: number;
    name: string;
    price: string | number;
    image: string;
  };
}

interface CartShowProps {
  items: CartItem[];
  store_settings: StoreSettings;
  exclusive_products: ExclusiveProduct[];
}

const CartShow = ({ items, store_settings, exclusive_products }: CartShowProps) => {
  const handleRemoveItem = (id: number) => {
    router.delete(`/cart/items/${id}`, {
      preserveScroll: true,
    });
  };

  const handleUpdateQuantity = (id: number, quantity: number) => {
    if (quantity > 0) {
      router.patch(`/cart/items/${id}`, { quantity }, {
        preserveScroll: true,
      });
    }
  };

  const subtotal = items.reduce((acc, item) => {
    return acc + Number(item.product.price) * item.quantity;
  }, 0);

  return (
    <MainLayout store_settings={store_settings} exclusiveProducts={exclusive_products}>
      <Head title="Carrito de Compras" />
      <Container className="py-12">
        <h1 className="text-3xl font-bold mb-8">Tu Carrito de Compras</h1>
        {items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="bg-card shadow-sm rounded-lg">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center p-4 border-b last:border-b-0">
                    <img src={item.product.image || 'https://picsum.photos/100'} alt={item.product.name} className="w-24 h-24 object-cover rounded-md mr-4" />
                    <div className="flex-grow">
                      <h2 className="font-semibold text-lg">{item.product.name}</h2>
                      <p className="text-muted-foreground">${(Number(item.product.price) || 0).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleUpdateQuantity(item.id, parseInt(e.target.value, 10))}
                        className="w-20 text-center"
                      />
                      <Button variant="outline" size="icon" onClick={() => handleRemoveItem(item.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-1">
              <div className="bg-card shadow-sm rounded-lg p-6">
                <h2 className="text-xl font-bold mb-4">Resumen del Pedido</h2>
                <div className="flex justify-between mb-2">
                  <p>Subtotal</p>
                  <p>${subtotal.toFixed(2)}</p>
                </div>
                <div className="flex justify-between mb-2">
                  <p>Envío</p>
                  <p>Gratis</p>
                </div>
                <div className="border-t my-4"></div>
                <div className="flex justify-between font-bold text-lg">
                  <p>Total</p>
                  <p>${subtotal.toFixed(2)}</p>
                </div>
                <Button className="w-full mt-6" size="lg">Proceder al Pago</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <h2 className="text-2xl font-semibold mb-4">Tu carrito está vacío</h2>
            <p className="text-muted-foreground mb-8">Parece que no has agregado nada a tu carrito todavía.</p>
            <Button onClick={() => router.get('/')}>Seguir Comprando</Button>
          </div>
        )}
      </Container>
    </MainLayout>
  );
};

export default CartShow;
